import { Box, Button, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { convertISODateTimeWithSeconds } from "@entrilia/shared/utilities/dateUtils";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch";
import { selectVersionHistoryPreviewData } from "../../../store/selectors/versionHistorySelectors";
import { keepPreviewConfiguration, revertToBackupConfiguration } from "../../../store/thunks/versionHistoryStateThunks";
import type { PreviewableVersion, UnsavedVersion } from "../../../store/types/versionHistoryState.types";

const isUnsavedVersion = (version: PreviewableVersion): version is UnsavedVersion => version.id === "unsaved";

const getVersionTitle = (version: PreviewableVersion) => {
  if (isUnsavedVersion(version)) {
    return "Unsaved Version";
  }
  return convertISODateTimeWithSeconds(version.updateAt);
};
const getVersionNumber = (version: PreviewableVersion) => {
  if (isUnsavedVersion(version) || version.versionNumber === 0) {
    return "";
  }
  return `#${version.versionNumber}`;
};
const VersionPreviewBar = () => {
  const previewData = useSelector(selectVersionHistoryPreviewData);
  const dispatch = useAppDispatch();

  if (!previewData) {
    return null;
  }

  const handleApply = () => {
    dispatch(keepPreviewConfiguration());
  };

  const handleClose = () => {
    dispatch(revertToBackupConfiguration());
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "3rem",
        left: "50%",
        bottom: "-1.5rem",
        position: "fixed",
        transform: "translate(-50%, -50%)",
        width: "auto",
        px: "2rem",
        py: "1rem",
        bgcolor: "info.main",
        color: "primary.contrastText",
        gap: 1,
        borderRadius: "4px 4px 0px 0px",
        boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.06)",
        zIndex: 1000,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: 2 }}>
        <Typography>
          Preview version {getVersionNumber(previewData)} -{" "}
          <Typography component={"span"} variant="subtitle2">
            {getVersionTitle(previewData)}
          </Typography>
        </Typography>
        <Button variant="outlined" sx={{ color: "inherit", borderColor: "inherit" }} onClick={handleApply}>
          Apply
        </Button>
        <Button variant="outlined" sx={{ color: "inherit", borderColor: "inherit" }} onClick={handleClose}>
          Close Preview
        </Button>
      </Stack>
    </Box>
  );
};

export default VersionPreviewBar;
